/**
 * By default, Remix will handle hydrating your app on the client for you.
 * You are free to delete this file if you'd like to, but if you ever want it revealed again, you can run `npx remix reveal` ✨
 * For more information, see https://remix.run/file-conventions/entry.client
 */

import { startTransition } from 'react';
import { hydrateRoot } from 'react-dom/client';

import { RemixBrowser } from '@remix-run/react';
import { Settings } from 'luxon';

import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';

if (import.meta.env?.PROD) {
  datadogRum.init({
    applicationId: '9257ac9f-874d-4157-adb4-d1f7dc8ac0b7',
    clientToken: 'pub9720a6d9f4b50509ef9f7c297e51234f',
    site: 'us5.datadoghq.com',
    service: 'hhos',
    env: process.env.NODE_ENV || 'development',
    version: import.meta.env.VITE_RELEASE_VERSION || undefined,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    // trackViewsManually: false,
    trackResources: true,
    trackLongTasks: true,
    allowedTracingUrls: [
      'https://hhos.honeyhomes.com',
      /\.honeyhomes\.com$/,
      'https://localhost:3000',
    ],
    defaultPrivacyLevel: 'allow',
    plugins: [reactPlugin({ router: false })],
  });
}

// Luxon formats in the browser's locale by default. Server-rendered text is produced in en-US, so
// any other browser locale would re-render dates differently during hydration.
Settings.defaultLocale = 'en-US';

// A failed hydration commits with the same mismatch queued ~100 times, and React's default handler
// (`reportError`) turns each into a window error event that Datadog records, so a handful of bad
// page loads trips the error-rate monitor. Report each distinct error once per burst instead.
const reportedRecoverableErrors = new Set<string>();

function reportRecoverableError(error: unknown) {
  const key = error instanceof Error ? error.message : String(error);
  if (reportedRecoverableErrors.has(key)) return;
  if (reportedRecoverableErrors.size === 0) {
    setTimeout(() => reportedRecoverableErrors.clear());
  }
  reportedRecoverableErrors.add(key);

  if (typeof window.reportError === 'function') {
    window.reportError(error);
  } else {
    console.error(error);
  }
}

const hydrate = () => {
  startTransition(() => {
    hydrateRoot(document, <RemixBrowser />, { onRecoverableError: reportRecoverableError });
  });
};

if (window.requestIdleCallback) {
  window.requestIdleCallback(hydrate);
} else {
  // Safari doesn't support requestIdleCallback
  // https://caniuse.com/requestidlecallback
  setTimeout(hydrate, 1);
}
